import { useMutation, useQueryClient } from "@tanstack/react-query";
import { userInfoKeys } from "Auth/Api/Queries/useUserInfoQuery";
import {
  type CreateIdentityFromMerchantCommand,
  type SignInResult,
  postAuthMigrateCreateIdentityFromMerchant,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

type Props = {
  onSuccess?: (result: SignInResult) => void;
  onError?: (error: unknown) => void;
};

/**
 * Creates a new Identity for a non-migrated Merchant/Employee user. Caller picks which of the
 * company's statutory representatives they are (or enters their details manually) and confirms
 * the statutory declaration. The chosen NaturalPerson becomes Identity.Party; the company stays
 * as IdentityUser.Party.
 */
export const useCreateIdentityFromMerchantMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      (data: CreateIdentityFromMerchantCommand) =>
        postAuthMigrateCreateIdentityFromMerchant(data),
    ),
    onSuccess: (result: SignInResult) => {
      queryClient.invalidateQueries({ queryKey: userInfoKeys.all });
      onSuccess?.(result);
    },
    onError,
  });
};
